import { useParams, Navigate } from "react-router-dom";
import { CITIES, SERVICES, COMPANY, FAQS } from "@/data/content";
import SEOHead from "@/components/SEOHead";
import AICalculator from "@/components/AICalculator";
import ContactForm from "@/components/ContactForm";
import { Link } from "react-router-dom";
import { Check, Phone, ArrowRight, MapPin } from "lucide-react";

export default function LocalSEOPage() {
  const { slug } = useParams();
  const city = CITIES.find((c) => c.slug === slug);
  if (!city) return <Navigate to="/" replace />;

  const localFAQ = [
    {
      q: `Bauen Sie auch in ${city.name}?`,
      a: `Ja, ${COMPANY.name} ist in ${city.name} regelmäßig für Privatkunden, Architekten und Bauträger tätig. Unsere Bauleitung kennt die örtlichen Behörden, Bauamt-Anforderungen und regionale Baupreise.`,
    },
    {
      q: `Was kostet ein Rohbau in ${city.name}?`,
      a: `Die Rohbau-Kosten in ${city.name} liegen je nach Bauart und Qualitätsstufe etwa zwischen 600 € und 950 € pro m². Mit unserem KI-Preisrechner erhalten Sie eine sofortige Indikation.`,
    },
    ...FAQS.slice(0, 4),
  ];

  const schema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": `${COMPANY.name} – Rohbau ${city.name}`,
    "description": `Premium Rohbauarbeiten in ${city.name} und Umgebung.`,
    "address": { "@type": "PostalAddress", "addressLocality": city.name, "addressRegion": "NRW", "addressCountry": "DE" },
    "telephone": COMPANY.phoneRaw,
    "areaServed": city.name,
    "priceRange": "€€€",
  };

  return (
    <>
      <SEOHead
        title={`Rohbau ${city.name} – ${COMPANY.name} | Festpreis & Termintreue`}
        description={`Rohbauunternehmen in ${city.name}: Beton-, Mauer-, Keller- und Fundamentarbeiten. Kostenfreier Preisrechner & 24h-Rückruf.`}
        schema={schema}
      />

      <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 bg-[#0C2749] text-white overflow-hidden">
        <div className="absolute inset-0 blueprint-bg-strong opacity-15" />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
          <p className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-[#2C80C8] mb-4">
            <MapPin className="w-3.5 h-3.5" /> Standort {city.name}
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tight max-w-4xl" style={{ fontFamily: "Outfit" }}>
            Rohbau in <span className="text-[#2C80C8]">{city.name}.</span>
          </h1>
          <p className="text-lg text-white/80 max-w-2xl mt-6 leading-relaxed">
            Wir realisieren in {city.name} und im Umkreis von 50 km Rohbauarbeiten für Einfamilienhäuser, Mehrfamilienhäuser und Gewerbeobjekte.
            Region: {city.region}.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Link to="/preisrechner" className="btn-shimmer inline-flex items-center gap-2 bg-white text-[#0C2749] hover:bg-[#F5F5F5] px-7 py-4 rounded-sm font-medium" data-testid="local-cta-calc">
              Preis in {city.name} berechnen <ArrowRight className="w-4 h-4" />
            </Link>
            <a href={`tel:${COMPANY.phoneRaw}`} className="inline-flex items-center gap-2 border border-white/30 hover:bg-white/10 px-7 py-4 rounded-sm font-medium" data-testid="local-cta-call">
              <Phone className="w-4 h-4" /> {COMPANY.phone}
            </a>
          </div>
        </div>
      </section>

      <section className="bg-white py-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0C2749] mb-6" style={{ fontFamily: "Outfit" }}>
              Ihr Rohbau-Spezialist in {city.name}
            </h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              {COMPANY.name} ist seit über zwei Jahrzehnten in {city.name} tätig. Wir kennen die regionalen Besonderheiten:
              das Baurecht, die Bodenverhältnisse, die typischen Architekturanforderungen und die Erwartungshaltung anspruchsvoller Bauherren.
            </p>
            <p className="text-slate-700 leading-relaxed mb-8">
              Ob Einfamilienhaus in einem ruhigen {city.name}er Stadtteil, Mehrfamilienhaus mit Tiefgarage oder Gewerbebau im Industriegebiet –
              wir liefern in {city.name} mit der Termintreue und Sichtbeton-Qualität, für die wir bekannt sind.
            </p>

            <h3 className="text-xl font-semibold text-[#0C2749] mb-4 mt-10" style={{ fontFamily: "Outfit" }}>Leistungen in {city.name}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {SERVICES.slice(0, 8).map((s) => (
                <Link key={s.slug} to={`/leistungen/${s.slug}`} className="flex items-center gap-3 p-4 bg-[#F5F5F5] hover:bg-[#0C2749] hover:text-white rounded-sm transition-colors group">
                  <Check className="w-4 h-4 text-[#2873B8] group-hover:text-[#2C80C8]" />
                  <span className="text-sm font-medium">{s.title} {city.name}</span>
                </Link>
              ))}
            </div>
          </div>
          <aside className="lg:col-span-5">
            <div className="bg-[#F5F5F5] p-8 border border-slate-200 sticky top-28 rounded-sm">
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#2873B8] mb-3">Schnelle Anfrage {city.name}</p>
              <p className="text-[#0C2749] font-semibold mb-6" style={{ fontFamily: "Outfit" }}>Kostenlose Erstberatung</p>
              <ContactForm source={`standort-${city.slug}`} compact />
            </div>
          </aside>
        </div>
      </section>

      <section className="bg-[#F5F5F5] py-24">
        <AICalculator compact />
      </section>

      <section className="bg-white py-24">
        <div className="max-w-[1100px] mx-auto px-6 md:px-12 lg:px-16">
          <h2 className="text-3xl md:text-4xl font-bold text-[#0C2749] mb-10" style={{ fontFamily: "Outfit" }}>FAQ – Rohbau in {city.name}</h2>
          <div className="border-t border-slate-200">
            {localFAQ.map((f, i) => (
              <details key={i} className="border-b border-slate-200 py-6 group" data-testid={`local-faq-${i}`}>
                <summary className="flex items-center justify-between cursor-pointer text-base md:text-lg font-medium text-[#0C2749] group-hover:text-[#2873B8]" style={{ fontFamily: "Outfit" }}>
                  {f.q}
                  <span className="text-[#2873B8] text-xl transition-transform group-open:rotate-45">+</span>
                </summary>
                <p className="text-sm md:text-base text-slate-600 leading-relaxed mt-3">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
